/**
 * Application frame: sidebar (modules → pages from the navigation registry), header (global
 * search across entities, notifications, language, user menu), content, footer.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon.tsx';
import { Link, isActive, useRoute } from '../lib/router.tsx';
import { useI18n } from '../lib/i18n.tsx';
import { useSession } from '../lib/session.tsx';
import { useMeta } from '../lib/meta.tsx';
import { BrandLogo, useBranding } from '../lib/branding.tsx';
import { useAi } from '../lib/ai.tsx';
import { AssistantPanel } from './Assistant.tsx';
import { api } from '../lib/api.ts';
import { useDebounced, useQuery } from '../lib/hooks.ts';
import { Menu, IconButton, useToast } from './ui.tsx';
import type { ListResult, NavModule, Row } from '../lib/types.ts';

const SIDEBAR_KEY = 'wc.sidebar';

function Sidebar({ nav, collapsed, onNavigate }: { nav: NavModule[]; collapsed: boolean; onNavigate: () => void }) {
  const { path } = useRoute();
  const { t, locale } = useI18n();
  const { branding, logo, mark } = useBranding();
  const tagline = locale === 'ar' ? branding.taglineAr ?? branding.tagline : branding.tagline;
  const [open, setOpen] = useState<string | null>(null);
  const activeModule = useMemo(() => nav.find((m) => m.code !== 'home' && isActive(path, m.path))?.code ?? null, [nav, path]);
  useEffect(() => { setOpen(activeModule); }, [activeModule]);
  return (
    <aside className="sidebar">
      <div className="brand">
        {logo
          ? <><span className="brand-plate"><img src={collapsed ? mark ?? logo : logo} alt={branding.appName} /></span><div className="name brand-name">{branding.appName}</div></>
          : <><img src="/logo.svg" alt="" className="logo" width={56} height={56} /><div className="name">{branding.appName}</div></>}
        {tagline && <div className="tagline">{tagline}</div>}
      </div>
      <nav className="nav">
        {nav.map((m) => {
          if (m.code === 'home') return m.pages.map((pg) => <Link key={pg.code} to={pg.path} className={path === pg.path ? 'active' : ''} onClick={onNavigate} data-nav={pg.code}><Icon name={pg.path === '/' ? m.icon : 'ScanLine'} /><span>{pg.label}</span></Link>);
          const isOpen = open === m.code && !collapsed;
          const active = activeModule === m.code;
          return (
            <div key={m.code} className={`group ${isOpen ? 'open' : ''}`}>
              <Link to={m.path} className={active && !isOpen ? 'active' : ''} onClick={(e) => { if (!collapsed) { e.preventDefault(); setOpen(isOpen ? null : m.code); } }} title={m.label}>
                <Icon name={m.icon} /><span>{m.label}</span><Icon name="ChevronRight" size={14} className="chev" />
              </Link>
              <div className="sub">
                {m.pages.map((p) => <Link key={p.code} to={p.path} className={path === p.path || (p.kind !== 'dashboard' && isActive(path, p.path)) ? 'active' : ''} onClick={onNavigate}><span>{p.label}</span></Link>)}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="nav-bottom">
        <Link to="/admin/settings" onClick={onNavigate}><Icon name="Settings" /><span>{t('nav.settings')}</span></Link>
        <a href="https://docs.woodcraft.local" target="_blank" rel="noreferrer" onClick={(e) => e.preventDefault()}><Icon name="CircleHelp" /><span>{t('nav.help')}</span></a>
      </div>
    </aside>
  );
}

function GlobalSearch() {
  const { t } = useI18n();
  const { entities } = useMeta();
  const { navigate } = useRoute();
  const [q, setQ] = useState('');
  const [openList, setOpenList] = useState(false);
  const dq = useDebounced(q.trim(), 250);
  const targets = useMemo(() => ['customer', 'supplier', 'project', 'contract', 'quotation', 'purchase_order', 'item', 'employee', 'document', 'sales_invoice', 'work_order'].filter((e) => entities.has(e)), [entities]);
  const { data } = useQuery(dq.length >= 2 ? `gsearch:${dq}` : null, async () => {
    const results = await Promise.all(targets.map(async (e) => ({ entity: e, res: await api.get<ListResult>(e, { search: dq, pageSize: 4 }).catch(() => null) })));
    return results.filter((r) => r.res && r.res.rows.length).map((r) => ({ entity: entities.get(r.entity)!, rows: r.res!.rows }));
  });
  const ref = useRef<HTMLDivElement>(null);
  const ai = useAi();
  const toast = useToast();
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpenList(false); };
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  // "find …" in words: the model turns them into the right list with filters
  const askAi = async () => {
    if (!dq || asking) return;
    setAsking(true);
    try { const r = await ai.search(dq); setOpenList(false); setQ(''); toast.success(r.explanation); navigate(r.path); }
    catch (e) { toast.error(e); } finally { setAsking(false); }
  };
  return (
    <div className="search" style={{ position: 'relative' }} ref={ref}>
      <Icon name="Search" />
      <input value={q} placeholder={ai.can('search') ? t('ai.searchPlaceholder') : t('nav.search')} onChange={(e) => { setQ(e.target.value); setOpenList(true); }} onFocus={() => setOpenList(true)} onKeyDown={(e) => { if (e.key === 'Enter' && ai.can('search')) { e.preventDefault(); void askAi(); } }} aria-label={t('common.search')} data-global-search />
      {openList && dq.length >= 2 && (
        <div className="search-results">
          {ai.can('search') && <a href="#" className="ask-ai" onClick={(e) => { e.preventDefault(); void askAi(); }} data-ask-ai><Icon name="Sparkles" size={14} />{asking ? t('ai.thinking') : t('ai.askSearch', { text: dq })}</a>}
          {!data ? <div className="empty">{t('common.loading')}</div> : data.length === 0 ? <div className="empty">{t('common.noResults')}</div> : data.map((g) => (
            <div key={g.entity.name}>
              <div className="group-l">{g.entity.labelPlural}</div>
              {g.rows.map((r) => <a key={String(r.id)} href={`${g.entity.route}/${r.id}`} onClick={(e) => { e.preventDefault(); setOpenList(false); setQ(''); navigate(`${g.entity.route}/${r.id}`); }}><span className="code">{g.entity.codeField ? String(r[g.entity.codeField] ?? '') : ''}</span><span className="truncate">{String(r[g.entity.titleField] ?? '')}</span></a>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AssistantButton() {
  const { t } = useI18n();
  const ai = useAi();
  const { path } = useRoute();
  if (!ai.can('assistant')) return null;
  return <IconButton icon="Sparkles" label={t('ai.assistant')} className={ai.open ? 'active' : ''} onClick={() => (ai.open ? ai.closeAssistant() : ai.openAssistant({ path }))} data-assistant-open />;
}

function Notifications() {
  const { t, dateTime } = useI18n();
  const { navigate } = useRoute();
  const toast = useToast();
  const count = useQuery('notif:count', () => api.get<{ unread: number }>('notifications/count'), { ttlMs: 30_000 });
  const [open, setOpen] = useState(false);
  const inbox = useQuery(open ? 'notif:inbox' : null, () => api.get<Row[]>('notifications', { limit: 20 }), { ttlMs: 0 });
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => { const i = setInterval(() => void count.refresh(), 60_000); return () => clearInterval(i); }, [count]);
  const markAll = async () => { try { await api.post('notifications/read', { all: true }); await Promise.all([count.refresh(), inbox.refresh()]); } catch (e) { toast.error(e); } };
  const openItem = async (n: Row) => {
    if (!n.read_at) { try { await api.post('notifications/read', { ids: [n.id] }); void count.refresh(); } catch { /* ignore */ } }
    setOpen(false);
    if (typeof n.link === 'string' && n.link) navigate(n.link);
  };
  const unread = count.data?.unread ?? 0;
  return (
    <div className="menu-wrap" ref={ref}>
      <IconButton icon="Bell" label={t('nav.notifications')} onClick={() => setOpen((o) => !o)} />
      {unread > 0 && <span className="badge-dot" style={{ position: 'absolute', top: 4, insetInlineEnd: 4 }}>{unread > 99 ? '99+' : unread}</span>}
      {open && (
        <div className="menu end notif-panel">
          <div className="panel-h"><span>{t('nav.notifications')}</span><span className="spacer" />{unread > 0 && <button type="button" className="btn ghost sm" onClick={markAll}>{t('nav.markAllRead')}</button>}</div>
          {!inbox.data ? <div className="empty">{t('common.loading')}</div> : inbox.data.length === 0 ? <div className="empty">{t('nav.noNotifications')}</div> : inbox.data.map((n) => (
            <a key={String(n.id)} href={typeof n.link === 'string' ? n.link : '#'} className={`item ${n.read_at ? '' : 'unread'}`} onClick={(e) => { e.preventDefault(); void openItem(n); }}>
              <span className="dot" /><span><b>{String(n.title)}</b>{n.body ? <div className="muted" style={{ fontSize: 12 }}>{String(n.body)}</div> : null}<div className="when">{dateTime(n.created_at)}</div></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n();
  const { session, logout, setLocale } = useSession();
  const { nav } = useMeta();
  const { navigate } = useRoute();
  const { branding } = useBranding();
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(SIDEBAR_KEY) === 'collapsed'; } catch { return false; } });
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggle = () => { if (window.innerWidth <= 800) setMobileOpen((o) => !o); else setCollapsed((c) => { try { localStorage.setItem(SIDEBAR_KEY, c ? 'open' : 'collapsed'); } catch { /* ignore */ } return !c; }); };
  const initials = (session?.user.fullName ?? '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const role = session?.jobTitle ?? session?.roles[0]?.name ?? '';
  return (
    <div className={`shell ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      <Sidebar nav={nav} collapsed={collapsed} onNavigate={() => setMobileOpen(false)} />
      <div className="main">
        <header className="header">
          <IconButton icon="Menu" label="Menu" onClick={toggle} />
          <span className="header-brand"><BrandLogo height={28} /></span>
          <GlobalSearch />
          <span className="spacer" />
          <IconButton icon="ScanLine" label={t('trace.scan')} onClick={() => navigate('/scan')} data-scan-open />
          <AssistantButton />
          <Notifications />
          <Menu align="end" trigger={<span className="lang"><Icon name="Languages" size={16} /> {locale.toUpperCase()} <Icon name="ChevronDown" size={14} /></span>}
            items={[{ label: 'English', icon: locale === 'en' ? 'Check' : undefined, onClick: () => void setLocale('en') }, { label: 'العربية', icon: locale === 'ar' ? 'Check' : undefined, onClick: () => void setLocale('ar') }]} />
          <Menu align="end" trigger={<span className="user"><span className="avatar">{initials}</span><span className="who"><b>{session?.user.fullName}</b><span>{role}</span></span><Icon name="ChevronDown" size={14} className="muted" /></span>}
            items={[
              { label: session?.tenant?.name ?? '', icon: 'Building2', disabled: true },
              { label: t('auth.profile'), icon: 'User', onClick: () => navigate('/profile') },
              { label: t('auth.changePassword'), icon: 'KeyRound', onClick: () => navigate('/profile?tab=password') },
              'sep',
              { label: t('auth.signOut'), icon: 'LogOut', onClick: () => void logout(), danger: true },
            ]} />
        </header>
        <main className="content">{children}</main>
        <AssistantPanel />
        <footer className="footer">
          <b>{branding.appName}</b><span>{t('app.version', { version: '1.0' })}</span><span>{session?.tenant?.name}</span>{branding.poweredBy && <span>{t('ui.poweredBy', { product: branding.productName })}</span>}
          <span className="spacer" />
          <span>{t('footer.egypt')}</span><span>{locale === 'ar' ? t('footer.arabic') : t('footer.english')}</span><span>{t('footer.privacy')}</span><span>{t('footer.terms')}</span>
        </footer>
      </div>
    </div>
  );
}
